import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";
import {PRICES_SEARCH_TEST_IDS} from "@pg-mono/test-ids";

import {SeoMainHeadline} from "../../../../atoms/SeoMainHeadline";
import {usePricesHubHeroCities} from "../../hooks/use_prices_hub_hero_cities";
import {PRICES_HUB_ANALYTICS_LOCATION} from "../../tracking/prices_hub_analytics";
import {PricesSearch} from "../prices_search/PricesSearch";
import {PricesHubHeroCityCard} from "./PricesHubHeroCityCard";
import {PricesHubViewSectionTracker} from "./PricesHubViewSectionTracker";

const heroImageMobile = require("../../images/prices_hub_hero_720.jpg");
const heroImageDesktopSmall = require("../../images/prices_hub_hero_1366.jpg");
const heroImageDesktopBig = require("../../images/prices_hub_hero_1920.jpg");

const HERO_HEADING_ID = "prices-hub-heading";

export const PricesHubHero = () => {
    const heroCities = usePricesHubHeroCities();

    return (
        <section className={heroSectionStyle} aria-labelledby={HERO_HEADING_ID}>
            <Container maxWidthVariant="lg">
                <div className={heroFrameStyle}>
                    <div className={heroImageClipStyle}>
                        <Picture
                            sources={[
                                {minWidthPX: 0, src: heroImageMobile, width: 720, height: 540},
                                {minWidthPX: 600, src: heroImageDesktopSmall, width: 1366, height: 1024},
                                {minWidthPX: 1366, src: heroImageDesktopBig, width: 1920, height: 1440}
                            ]}
                            alt=""
                            className={heroImageStyle}
                            fit="center center"
                            fetchPriority="high"
                        />
                        <div className={heroOverlayStyle} aria-hidden="true" />
                    </div>

                    <div className={heroContentStyle}>
                        <SeoMainHeadline id={HERO_HEADING_ID} variant="headline_1" fontFamily="secondary" align="center" className={heroHeadingStyle}>
                            Ceny mieszkań w Polsce
                        </SeoMainHeadline>

                        <Text as="p" variant="body_copy_1" align="center" className={heroLeadStyle}>
                            Sprawdź, ile kosztują mieszkania w Twojej okolicy – porównaj ceny ofertowe i ceny transakcyjne.
                        </Text>

                        <div className={heroSearchStyle}>
                            <PricesSearch location={PRICES_HUB_ANALYTICS_LOCATION} dataTestId={PRICES_SEARCH_TEST_IDS.ROOT} />
                        </div>

                        {heroCities.length > 0 ? (
                            <div className={popularStyle}>
                                <PricesHubViewSectionTracker sectionName="popular_searches" />

                                <Text as="p" variant="info_txt_1" className={popularLabelStyle}>
                                    Najpopularniejsze wyszukiwania:
                                </Text>

                                <ul className={popularListStyle}>
                                    {heroCities.map((city) => (
                                        <li key={city.slug} className={popularItemStyle}>
                                            <PricesHubHeroCityCard city={city} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Container>
        </section>
    );
};

const heroSectionStyle = css({
    pt: 1.5,
    pb: 3,

    md: {
        pt: 0,
        pb: 0
    }
});

const heroFrameStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    width: "100%",
    minHeight: "43.9rem",
    padding: 2,
    borderRadius: 3,

    md: {
        minHeight: "48.8rem",
        pt: 8,
        pr: 10,
        pb: 9,
        pl: 10
    }
});

const heroImageClipStyle = css({
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: 3
});

const heroImageStyle = css({
    width: "100%",
    height: "100%",

    "& img": {
        width: "100%",
        height: "100%",
        maxWidth: "none",
        objectFit: "cover"
    }
});

const heroOverlayStyle = css({
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(23, 23, 28, 0.5) 0%, rgba(23, 23, 28, 0.88) 100%)",

    md: {
        background: "linear-gradient(180deg, rgba(35, 35, 45, 0.46) 0%, rgba(35, 35, 45, 0.58) 58%, rgba(35, 35, 45, 0.9) 100%)"
    }
});

const heroContentStyle = css({
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    alignItems: "center",
    justifyContent: "center",
    width: "100%"
});

const heroHeadingStyle = css({
    margin: 0,
    marginBottom: "1rem",
    color: "var(--colors-white)",
    fontSize: "2.737rem",
    fontWeight: 700,
    lineHeight: "3.6rem",
    textShadow: "0 2px 16px rgba(0, 0, 0, 0.3)",

    md: {
        fontSize: "4.8rem",
        lineHeight: "5.2rem",
        textShadow: "0 2px 20px rgba(0, 0, 0, 0.35)"
    }
});

const heroLeadStyle = css({
    maxWidth: "86rem",
    margin: 0,
    marginBottom: "1.4rem",
    color: "var(--colors-white)",

    md: {
        marginBottom: 4.5
    }
});

const heroSearchStyle = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginBottom: "1.4rem",

    md: {
        maxWidth: "77.6rem",
        marginBottom: 3.5
    }
});

const popularStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",

    md: {
        maxWidth: "99.8rem"
    }
});

const popularLabelStyle = css({
    margin: 0,
    color: "rgba(255, 255, 255, 0.8)"
});

const popularListStyle = css({
    display: "flex",
    gap: 1,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        overflowX: "visible"
    }
});

const popularItemStyle = css({
    flex: "0 0 auto",
    width: "min(23.4rem, 100%)",
    scrollSnapAlign: "start",

    md: {
        flex: "1 1 0",
        width: "auto",
        minWidth: 0
    }
});
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";
import {PRICES_SEARCH_TEST_IDS} from "@pg-mono/test-ids";

import {SeoMainHeadline} from "../../../../atoms/SeoMainHeadline";
import {usePricesHubHeroCities} from "../../hooks/use_prices_hub_hero_cities";
import {PRICES_HUB_ANALYTICS_LOCATION} from "../../tracking/prices_hub_analytics";
import {PricesSearch} from "../prices_search/PricesSearch";
import {PricesHubHeroCityCard} from "./PricesHubHeroCityCard";
import {PricesHubViewSectionTracker} from "./PricesHubViewSectionTracker";

const heroImageMobile = require("../../images/prices_hub_hero_720.jpg");
const heroImageDesktopSmall = require("../../images/prices_hub_hero_1366.jpg");
const heroImageDesktopBig = require("../../images/prices_hub_hero_1920.jpg");

const HERO_HEADING_ID = "prices-hub-heading";

export const PricesHubHero = () => {
    const heroCities = usePricesHubHeroCities();

    return (
        <section className={heroSectionStyle} aria-labelledby={HERO_HEADING_ID}>
            <Container maxWidthVariant="lg">
                <div className={heroFrameStyle}>
                    <div className={heroImageClipStyle}>
                        <Picture
                            sources={[
                                {minWidthPX: 0, src: heroImageMobile, width: 720, height: 540},
                                {minWidthPX: 600, src: heroImageDesktopSmall, width: 1366, height: 1024},
                                {minWidthPX: 1366, src: heroImageDesktopBig, width: 1920, height: 1440}
                            ]}
                            alt=""
                            className={heroImageStyle}
                            fit="center center"
                            fetchPriority="high"
                        />
                        <div className={heroOverlayStyle} aria-hidden="true" />
                    </div>

                    <div className={heroContentStyle}>
                        <SeoMainHeadline id={HERO_HEADING_ID} variant="headline_1" fontFamily="secondary" align="center" className={heroHeadingStyle}>
                            Ceny mieszkań w Polsce
                        </SeoMainHeadline>

                        <Text as="p" variant="body_copy_1" align="center" className={heroLeadStyle}>
                            Sprawdź, ile kosztują mieszkania w Twojej okolicy – porównaj ceny ofertowe i ceny transakcyjne.
                        </Text>

                        <div className={heroSearchStyle}>
                            <PricesSearch location={PRICES_HUB_ANALYTICS_LOCATION} dataTestId={PRICES_SEARCH_TEST_IDS.ROOT} />
                        </div>

                        {heroCities.length > 0 ? (
                            <div className={popularStyle}>
                                <PricesHubViewSectionTracker sectionName="popular_searches" />

                                <Text as="p" variant="info_txt_1" className={popularLabelStyle}>
                                    Najpopularniejsze wyszukiwania:
                                </Text>

                                <ul className={popularListStyle}>
                                    {heroCities.map((city) => (
                                        <li key={city.slug} className={popularItemStyle}>
                                            <PricesHubHeroCityCard city={city} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Container>
        </section>
    );
};

const heroSectionStyle = css({
    pt: 1.5,
    pb: 3,

    md: {
        pt: 0,
        pb: 0
    }
});

const heroFrameStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    width: "100%",
    minHeight: "43.9rem",
    padding: 2,
    borderRadius: 3,

    md: {
        minHeight: "48.8rem",
        pt: 8,
        pr: 10,
        pb: 9,
        pl: 10
    }
});

const heroImageClipStyle = css({
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: 3
});

const heroImageStyle = css({
    width: "100%",
    height: "100%",

    "& img": {
        width: "100%",
        height: "100%",
        maxWidth: "none",
        objectFit: "cover"
    }
});

const heroOverlayStyle = css({
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(23, 23, 28, 0.5) 0%, rgba(23, 23, 28, 0.88) 100%)",

    md: {
        background: "linear-gradient(180deg, rgba(35, 35, 45, 0.46) 0%, rgba(35, 35, 45, 0.58) 58%, rgba(35, 35, 45, 0.9) 100%)"
    }
});

const heroContentStyle = css({
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    alignItems: "center",
    justifyContent: "center",
    width: "100%"
});

const heroHeadingStyle = css({
    margin: 0,
    marginBottom: "1rem",
    color: "var(--colors-white)",
    fontSize: "2.737rem",
    fontWeight: 700,
    lineHeight: "3.6rem",
    textShadow: "0 2px 16px rgba(0, 0, 0, 0.3)",

    md: {
        fontSize: "4.8rem",
        lineHeight: "5.2rem",
        textShadow: "0 2px 20px rgba(0, 0, 0, 0.35)"
    }
});

const heroLeadStyle = css({
    maxWidth: "86rem",
    margin: 0,
    marginBottom: "1.4rem",
    color: "var(--colors-white)",

    md: {
        marginBottom: 4.5
    }
});

const heroSearchStyle = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    marginBottom: "1.4rem",

    md: {
        maxWidth: "77.6rem",
        marginBottom: 3.5
    }
});

const popularStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",

    md: {
        maxWidth: "99.8rem"
    }
});

const popularLabelStyle = css({
    margin: 0,
    color: "rgba(255, 255, 255, 0.8)"
});

const popularListStyle = css({
    display: "flex",
    gap: 1,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        overflowX: "visible"
    }
});

const popularItemStyle = css({
    flex: "0 0 auto",
    width: "min(23.4rem, 100%)",
    scrollSnapAlign: "start",

    md: {
        flex: "1 1 0",
        width: "auto",
        minWidth: 0
    }
});
